import { useNavigate } from 'react-router-dom';
import CustomBreadcrumbs from '../../components/common/CustomBreadcrumbs';
import OutputLeftSide from '../../components/feeOutput.js/OutputLeftSide';
import OutputRightSide from '../../components/feeOutput.js/OutputRightSide';
import { selectFeesFormData, selectFeesResponse } from '../../redux-state/selectors';
import html2pdf from 'html2pdf.js/dist/html2pdf.min.js';
import { useSelector } from 'react-redux';
import DownloadIcon from '@mui/icons-material/Download';
import SecondaryButton from '../../components/common/button/SecondaryButton';
import PrimaryButton from '../../components/common/button/PrimaryButton';
import { generateFeesReport } from '../../components/utility/generateFeesReport';

export default function FeeOutput() {
  const feesFormData = useSelector(selectFeesFormData) || {};
  const navigate = useNavigate();
  const response = useSelector(selectFeesResponse);

  const convertToPdf = async () => {
    if (!response || Object.keys(response).length === 0) {
      return;
    }
    const content = generateFeesReport(feesFormData, response);

    const pdfOptions = {
      filename: (response["TransactionID"] ? `${response["TransactionID"]}.pdf` : 'feesOutput.pdf'),
      image: { type: 'jpeg', quality: 0.98 }, // <-- controls embedded image quality
      jsPDF: {
        unit: 'in',
        format: 'letter', // equivalent to [8.5, 11], just clearer
        orientation: 'portrait',
      },
      html2canvas: {
        scale: 2, // <-- default can render blurry text. 2 sharpens it
        useCORS: true, // if you load any external images
      },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }, // <-- if content spans multiple pages
    };

    // Convert generated HTML report to PDF
    const instance = html2pdf().set(pdfOptions).from(content);

    // Capture PDF as a blob
    const pdfBlob = await instance.output('blob'); 
    
    // Download the PDF report
    instance.save();

    // Gather the required JSON metadata
    const vin = feesFormData.vinCode || 'UNKNOWN_VIN'; 
    const fileName = pdfOptions.filename;
    let callType = ""
    if (fileName.includes("REF")){
      callType = "Refi-Fees"
    }else{
      callType = "Fees"
    }

    //Create FormData object
    const formData = new FormData();
    
    // Append the PDF Blob as a file
    formData.append('file', pdfBlob, fileName); 
    
    // Append the required JSON metadata
    formData.append('fileName', fileName);
    formData.append('call_type', callType);
    formData.append('vin', vin);

    // Send the FormData to the backend send_to_S3 endpoint
    try {
      const SERVER_URL = process.env.REACT_APP_API_URL;
      const backendUrl = `${SERVER_URL}/api/send_to_S3`; 
      const CSRFURL = `${SERVER_URL}/api/get-csrf-token`
      
      const response = await fetch(CSRFURL, {
        method: 'GET',
        credentials: 'include',
      })

      const response_data = await response.json();

      const CSRF_token_value = response_data.csrf_token; 

      if (!CSRF_token_value) {
          console.error("CSRF token not found.");
          return; // Exit or handle error
      }

      const result = await fetch(backendUrl, {
        method: 'POST',
        headers: {
          "X-Csrftoken": CSRF_token_value},
        body: formData,
        credentials: 'include'
      });
      
      const data = await result.json(); 
      console.log("S3 Upload Success:", data);
      
    } catch (error) {
      console.error("S3 Upload Failed:", error);
    }
  };

  return (
    <div className='p-4 lg:p-8 min-h-[900px]'>
      <div className='flex flex-row justify-between'>
        <div className='mb-4'>
          <CustomBreadcrumbs title={"Fees"} subtitle={"Fees Output"} />
        </div>
        <div>
          <SecondaryButton
            text="Back"
            onClick={() => navigate(-1)}
          />
        </div>
      </div>

      {/* Title and Download share a single row, with the transaction id
          directly beneath, so the header doesn't leave empty bands of
          vertical space above and below the id. */}
      <div className='flex flex-row justify-between items-center gap-4'>
        <div className='text-3xl font-bold text-gray-900'> Fees Output </div>
        {/* Small top margin so Download doesn't butt up against the Back
            button sitting directly above it. */}
        <div className='mt-3'>
          <PrimaryButton
            text={<><DownloadIcon sx={{ mr: '6px' }} /> Download</>}
            onClick={convertToPdf}
          />
        </div>
      </div>
      <div className='text-gray-600 break-all mb-4'> {response["TransactionID"]} </div>

      <div className='flex flex-col xl:flex-row w-full gap-6'>
        <div className='w-full xl:w-1/2 min-w-0'>
          <OutputLeftSide />
        </div>

        <div className="hidden xl:block w-px bg-gray-300" />

        <div className='w-full xl:w-1/2 min-w-0 xl:mt-3'>
          <OutputRightSide />
        </div>
      </div>
    </div>
  );
}